import { useState, useMemo, useCallback } from "react";
import {
  CreateTaskResult,
  ErrorCode,
} from "../../../../../application/domain/application/application-removal-task/create-task-result";
import dayjs from "dayjs";
import apiClient from "../../../../../application/apis/api-client";
import { toast } from "react-toastify";
import { SmartApplyR } from "../../route";
import { useHistory } from "react-router-dom";
import { reportAsyncError, SmartApplyApiError } from "@graffer-inc/smart-apply";
import { CsvFileType } from "../../../../molecules/csv-uploader";

export const useApplicationRemovalTaskRegisterHook = () => {
  const history = useHistory();
  const [removeDate, setRemoveDate] = useState<Date | null>(null);
  const [reason, setReason] = useState("");
  const [file, setFile] = useState<CsvFileType | undefined>();
  const [createTaskResult, setCreateTaskResult] = useState<
    CreateTaskResult | undefined
  >(undefined);
  const [targetProcedureName, setTargetProcedureName] = useState("");
  const [errorCode, setErrorCode] = useState<ErrorCode | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const removeDateError = useMemo(() => {
    if (
      removeDate &&
      (dayjs(removeDate).isBefore(dayjs().add(1, "day"), "day") ||
        dayjs(removeDate).isAfter(dayjs().add(14, "day"), "day"))
    ) {
      return "削除予定日は明日以降、14日以内の日付を選択してください。";
    }
    return undefined;
  }, [removeDate]);

  const reasonError = useMemo(() => {
    if (reason.length > 200) {
      return "理由は200文字以内で入力してください。";
    }
    return undefined;
  }, [reason]);

  const validInputs = useMemo(() => {
    return Boolean(
      removeDate && reason && file && !removeDateError && !reasonError,
    );
  }, [file, reason, reasonError, removeDate, removeDateError]);

  const resetResult = () => {
    setCreateTaskResult(undefined);
  };

  const registerTask = useCallback(
    async (isDryRun: boolean) => {
      if (!removeDate || !reason || !file) {
        toast.error("全ての項目を入力してください。");
        return;
      }

      try {
        setIsLoading(true);
        const { data: result } = await apiClient.registerRemovalApplicationTask(
          {
            reason,
            scheduledRemoveDate: removeDate,
            csv: file.data,
            isDryRun,
          },
        );
        setCreateTaskResult(result);
        setErrorCode(undefined);

        if (isDryRun) {
          if (result.ok && result.summary) {
            const { data: procedure } = await apiClient.fetchProcedure(
              result.summary.procedureID,
            );
            setTargetProcedureName(procedure.name);
          } else {
            setTargetProcedureName("");
          }
        } else {
          if (!result.ok) {
            throw new Error(
              `failed to register application removal task: ${
                result.applicationErrors
                  ? result.applicationErrors
                      .map((error) => {
                        return `ID: ${error.applicationID}, code: ${error.errorCode}, detail: ${error.errDetail}`;
                      })
                      .join(", ")
                  : "unknown error"
              }`,
            );
          }
          toast.success("申請削除タスクの登録に成功しました。");
          history.push(SmartApplyR.ApplicationRemovalTaskList.link({}));
        }
      } catch (e) {
        if (!isDryRun) {
          if (
            SmartApplyApiError.isErrorCode(e, "bad_request.version_outdated")
          ) {
            toast.error(
              "申請削除タスクの登録に失敗しました。画面を更新して再度登録してください。",
            );
            return;
          }
          reportAsyncError(e);
          toast.error("予期しないエラーが発生しました。");
          return;
        }
        setCreateTaskResult(undefined);
        if (
          SmartApplyApiError.isErrorCode(
            e,
            "bad_request.multiple_procedure_ids_found",
          )
        ) {
          setErrorCode("MultipleProcedureIDsFound");
          return;
        }
        if (
          SmartApplyApiError.isErrorCode(e, "bad_request.too_many_applications")
        ) {
          setErrorCode("TooManyApplications");
          return;
        }
        if (
          SmartApplyApiError.isErrorCode(e, "bad_request.invalid_csv_format")
        ) {
          setErrorCode("InvalidCSV");
          return;
        }
        reportAsyncError(e);
        setErrorCode("UnexpectedError");
      } finally {
        setIsLoading(false);
      }
    },
    [file, history, reason, removeDate],
  );

  return {
    removeDate,
    setRemoveDate,
    reason,
    setReason,
    file,
    setFile,
    createTaskResult,
    errorCode,
    targetProcedureName,
    removeDateError,
    reasonError,
    validInputs,
    resetResult,
    validate: () => registerTask(true),
    submit: () => registerTask(false),
    isLoading,
  };
};
